import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { TicketBatch } from "../../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import {
  defaultHalfPriceAmount,
  defaultHalfPriceQuantity,
} from "../../../../../../../shared/helpers/half-price/half-price.helper";
import { maxTicketsQuantityValidator } from "./validators/max-tickets-quantity.validator";
import { minimumTicketPrice } from "../../../../../../../shared/helpers/payment-minimum.helper";

type TicketInBatch = TicketBatch["tickets"][number];

type BuildModalTicketFormParams = {
  formBuilder: FormBuilder;
  ticket?: TicketInBatch;
  halfTicket?: TicketInBatch;
  isTicketFree: boolean;
  withHalfPricePair: boolean;
};

export type ModalTicketForm = FormGroup<{
  id: FormControl<number | null | undefined>;
  ticketTitle: FormControl<string>;
  ticketDescription: FormControl<string>;
  quantity: FormControl<number>;
  price: FormControl<number | string>; // pode ser string quando "GRATIS" disabled
  halfPrice: FormControl<boolean>;
  halfPriceId: FormControl<number | null | undefined>;
  halfPriceQuantity: FormControl<number>;
  halfPricePrice: FormControl<number | string>;
  minTickets: FormControl<number>;
  maxTickets: FormControl<number>;
  availability: FormControl<string>;
}>;

export function buildModalTicketForm(
  params: BuildModalTicketFormParams,
): ModalTicketForm {
  const { formBuilder, ticket, halfTicket, isTicketFree, withHalfPricePair } =
    params;

  const fullQuantity = ticket?.quantity ?? 0;
  const fullPrice = ticket?.price ?? 0;

  const ticketForm = formBuilder.group(
    {
      id: formBuilder.control<number | null | undefined>(ticket?.id),

      ticketTitle: formBuilder.control<string>(ticket?.title ?? "", {
        validators: [Validators.required],
      }),

      ticketDescription: formBuilder.control<string>(
        ticket?.description ?? "",
        {
          validators: [Validators.required],
        },
      ),

      quantity: formBuilder.control<number>(fullQuantity, {
        validators: [Validators.required, Validators.min(1)],
      }),

      price: isTicketFree
        ? formBuilder.control<number | string>({
            value: "GRATIS",
            disabled: true,
          })
        : formBuilder.control<number | string>(fullPrice, {
            validators: [Validators.required, minimumTicketPrice],
          }),

      halfPrice: formBuilder.control<boolean>(ticket?.half_price ?? false),
      halfPriceId: formBuilder.control<number | null | undefined>(halfTicket?.id),
      halfPriceQuantity: formBuilder.control<number>(
        halfTicket?.quantity ??
          (withHalfPricePair && fullQuantity
            ? defaultHalfPriceQuantity(fullQuantity)
            : 0),
        {
          validators: [Validators.required, Validators.min(1)],
        },
      ),
      halfPricePrice: formBuilder.control<number | string>(
        halfTicket?.price ??
          (withHalfPricePair && fullPrice
            ? defaultHalfPriceAmount(fullPrice)
            : 0),
        {
          validators: [Validators.required, minimumTicketPrice],
        },
      ),

      minTickets: formBuilder.control<number>(ticket?.min_per_buyer ?? 1, {
        validators: [Validators.required, Validators.min(1)],
      }),

      maxTickets: formBuilder.control<number>(ticket?.max_per_buyer ?? 5, {
        validators: [Validators.required, Validators.min(1)],
      }),

      availability: formBuilder.control<string>(
        ticket?.availability ?? "general public",
        {
          validators: [Validators.required],
        },
      ),
    },
    {
      validators: [maxTicketsQuantityValidator()],
    },
  ) as ModalTicketForm;

  if (!withHalfPricePair) {
    ticketForm.controls.halfPriceId.disable({ emitEvent: false });
    ticketForm.controls.halfPriceQuantity.disable({ emitEvent: false });
    ticketForm.controls.halfPricePrice.disable({ emitEvent: false });
  }

  return ticketForm;
}
